/**
 * EntityNavigator — стек окон сущностей.
 *
 * `open({kind, ...})` кладёт окно макета, товара, черновика или карточки
 * площадки поверх текущего экрана, «Назад» (Alt+←, кнопка, крестик, Esc)
 * снимает верхнее. Отрисовывается только верхнее окно стека: нижние
 * пересобираются при возврате, поэтому несохранённые правки в окне, которое
 * ушло под цепочку, защищает само окно (оно спрашивает до перехода).
 *
 * Окно страницы (то, что открыто через ?card=… или клик по строке) стеку не
 * принадлежит и остаётся под навигатором.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { EntityWindow } from './EntityWindowHost'
import {
  EntityNavigatorContext,
  sameTarget,
  useEntityNavigator,
  type EntityNavigator,
  type EntityTarget,
} from './navigatorContext'

export { useEntityNavigator }
export type { EntityNavigator, EntityTarget }

export const EntityNavigatorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<EntityTarget[]>([])
  const location = useLocation()

  const open = useCallback((target: EntityTarget) => {
    setStack((current) => {
      const top = current[current.length - 1]
      return top && sameTarget(top, target) ? current : [...current, target]
    })
  }, [])

  const back = useCallback(() => {
    setStack((current) => (current.length === 0 ? current : current.slice(0, -1)))
  }, [])

  const closeAll = useCallback(() => setStack([]), [])

  // Уход на другую страницу закрывает окна: они принадлежали прежнему экрану.
  useEffect(() => {
    setStack((current) => (current.length === 0 ? current : []))
  }, [location.pathname])

  const value = useMemo<EntityNavigator>(
    () => ({ open, back, depth: stack.length, available: true }),
    [open, back, stack.length],
  )

  const top = stack[stack.length - 1] ?? null
  // Ключ окна включает глубину: переход вперёд пересобирает окно с нуля,
  // а не подставляет новые данные в состояние предыдущего.
  const topKey = top ? `${stack.length}:${JSON.stringify(top)}` : null

  return (
    <EntityNavigatorContext.Provider value={value}>
      {children}
      {top && topKey && (
        /*
          relative + z-index создают контекст наложения выше окон страницы и
          их вложенных диалогов: fixed-потомки рисуются внутри него.
        */
        <div className="relative z-[70]" data-entity-navigator-depth={stack.length}>
          <EntityWindow
            key={topKey}
            target={top}
            // Любое окно, открытое через навигатор, лежит поверх чего-то: другого
            // окна стека или окна страницы. «Назад» снимает его и возвращает нижнее.
            canGoBack
            onBack={back}
            onOpen={open}
            onCloseAll={closeAll}
          />
        </div>
      )}
    </EntityNavigatorContext.Provider>
  )
}

export default EntityNavigatorProvider
